import { useQueries, useQuery } from '@tanstack/react-query';
import { fetchCctvList } from '@/domains/traffic-monitoring/apis/cctv-stream';
import type { CctvListItem } from '@/domains/traffic-monitoring/types/cctv.types';

const STALE_TIME_MS = 1000 * 60 * 1;

// GET /cctv-list (CCTV Dashboard
export const useCctvList = (filterParams: string = '') => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['cctvList', filterParams],
    queryFn: () => fetchCctvList(filterParams),
    staleTime: STALE_TIME_MS,
  });

  return {
    cctvList: data?.data?.cctv_list ?? [],
    isLoading,
    isError,
  };
};

// GET /cctv?cctv_id={id} - single camera (CCTVDetailDashboard).
// Same endpoint as the list; the filter narrows cctv_list to one item.
export const useCctvDetail = (cctvId?: number) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['cctvDetail', cctvId],
    queryFn: () => fetchCctvList(`cctv_id=${cctvId}`),
    enabled: cctvId != null,
    staleTime: STALE_TIME_MS,
  });

  return {
    cctv: (data?.data?.cctv_list?.[0] ?? null) as CctvListItem | null,
    isLoading,
    isError,
  };
};

// Cameras within radiusMeters of any given position (e.g. one per ongoing
// incident on PSCDashboard), one server-side radius query per position -
// filtering is done in get_cctv_list.py, not client-side. Results are
// deduped by cctv_id in case two positions' radii overlap.
export const useCctvNearIncidents = (
  positions: { lat: number; lng: number }[],
  radiusMeters = 1000
) => {
  return useQueries({
    queries: positions.map((pos) => ({
      queryKey: ['cctvNearIncident', pos.lat, pos.lng, radiusMeters],
      queryFn: async () => {
        const res = await fetchCctvList(`lat=${pos.lat}&lon=${pos.lng}&radius_m=${radiusMeters}`);
        return res?.data?.cctv_list ?? [];
      },
      staleTime: STALE_TIME_MS,
    })),
    combine: (queries) => {
      const seen = new Map<number, CctvListItem>();
      for (const query of queries) {
        for (const item of (query.data as CctvListItem[]) ?? []) {
          seen.set(item.cctv_id, item);
        }
      }
      return {
        cctvList: Array.from(seen.values()),
        isLoading: queries.some((query) => query.isLoading),
      };
    },
  });
};
